/*  CWL DJ Mixer browser demo: the parameters, for the panel.

    The panel moves knobs in the plugin's normalised units (0 to 1, exactly what the plate's knobs
    send the host) and prints the plugin's own read-outs. Both need the parameter ranges, so they are
    here, ported from:

      Source/Parameters.cpp createLayout      ranges, skews, intervals, defaults, the toText lambdas
      Source/dsp/Mappings.h                   the tapers, the dead zone, the filter and effect curves
      Source/ui/plate/PlatePanel.cpp          knobValueText, knobLabelText, the glass and the meters
      JUCE 8.0.15                             NormalisableRange, RangedAudioParameter's snap, the
                                              choice and bool parameters, String (float, places),
                                              roundToInt

    in float arithmetic (Math.fround after every operation, as the C++ does it in float), so the
    strings come out as the plugin prints them. web/gates/readouts.mjs checks every string here
    against the plugin's getText over two thousand knob positions per parameter.

    The audio never uses these numbers: the knob position itself goes to the Wasm module, which maps
    it with the C++ (web/bridge/CwlMixerCore.h). These are for drawing.

    Loads as a classic script (window.CwlParams) or under Node (module.exports).
*/

(function (root)
{
    'use strict';

    const f = Math.fround;
    const powf = (a, b) => f (Math.pow (f (a), f (b)));
    const expf = (a) => f (Math.exp (f (a)));
    const logf = (a) => f (Math.log (f (a)));
    const jlimit = (lo, hi, v) => v < lo ? lo : (hi < v ? hi : v);

    // ---- Mappings.h ----------------------------------------------------------------------

    const kEqFloorDb = -26, kTrimFloorDb = -80, kTrimMaxDb = 9, kFaderFloorDb = -80;
    const kKillTaperExp = f (1.684), kTrimTaperExp = f (2.5), kFaderTaperExp = f (2.5);
    const kDeadZone = f (0.02);

    function killTaperToDb (norm, floorDb)
    {
        const n = jlimit (0, 1, f (norm));
        if (n >= 0.5) return f (f (f (n - 0.5) * 2) * 6);
        const t = f (1 - f (n * 2));
        return f (floorDb * powf (t, kKillTaperExp));
    }

    function dbToKillTaper (db, floorDb)
    {
        if (db >= 0) return f (0.5 + f (jlimit (0, 1, f (db / 6)) * 0.5));
        const t = powf (jlimit (0, 1, f (db / floorDb)), f (1 / kKillTaperExp));
        return f (0.5 * f (1 - t));
    }

    function trimTaperToDb (norm)
    {
        const n = jlimit (0, 1, f (norm));
        if (n >= 0.5) return f (f (f (n - 0.5) * 2) * kTrimMaxDb);
        const t = f (1 - f (n * 2));
        return f (kTrimFloorDb * powf (t, kTrimTaperExp));
    }

    function dbToTrimTaper (db)
    {
        if (db >= 0) return f (0.5 + f (jlimit (0, 1, f (db / kTrimMaxDb)) * 0.5));
        const t = powf (jlimit (0, 1, f (db / kTrimFloorDb)), f (1 / kTrimTaperExp));
        return f (0.5 * f (1 - t));
    }

    const faderTaperToDb = (norm) => f (kFaderFloorDb * powf (f (1 - jlimit (0, 1, f (norm))), kFaderTaperExp));
    const dbToFaderTaper = (db) => f (1 - powf (jlimit (0, 1, f (db / kFaderFloorDb)), f (1 / kFaderTaperExp)));

    const logInterp = (lo, hi, t) => f (lo * powf (f (hi / lo), jlimit (0, 1, f (t))));

    function deadZoneAmount (bipolar)
    {
        const v = jlimit (-1, 1, f (bipolar));
        if (Math.abs (v) <= kDeadZone) return 0;
        const sign = v < 0 ? -1 : 1;
        return f (f (sign * f (Math.abs (v) - kDeadZone)) / f (1 - kDeadZone));
    }

    function channelFilterCutoff (corrected)
    {
        const d = jlimit (0, 1, Math.abs (corrected));
        return corrected < 0 ? logInterp (20000, 20, d) : logInterp (20, 20000, d);
    }

    const colorSpaceDecaySeconds = (p) => logInterp (f (0.3), 8, p);
    const colorDubEchoFeedback = (p) => jlimit (0, f (0.85), f (p * f (0.85)));
    const colorCrushBitDepth = (p) => f (16 + f (p * f (4 - 16)));
    const jmapf = (v, lo, hi) => f (lo + f (v * f (hi - lo)));
    const bogThrowFromAmount = (a) => f (jlimit (0, 1, f (a)) * 127);
    const bogDecaySecondsFromDecay = (d) => f (f (0.2) * powf (f (f (60) / f (0.2)), jlimit (0, 1, f (d))));

    // ---- the parameters (Parameters.cpp; the same table as web/bridge/CwlMixerCore.h) --------

    const L = 'linear', S = 'skewed', K = 'kill', T = 'trim', FD = 'fader', C = 'choice', B = 'bool';

    const SPECS =
    [
        ['plugin_bypass',            B,  0,      1,     1,     0,     0],
        ['trim_gain_db',             T,  -80,    9,     0,     0,     0],
        ['eq_low_gain_db',           K,  -26,    6,     0,     0,     0],
        ['eq_mid_gain_db',           K,  -26,    6,     0,     0,     0],
        ['eq_high_gain_db',          K,  -26,    6,     0,     0,     0],
        ['channel_filter_amount',    L,  -1,     1,     0.001, 0,     0],
        ['channel_filter_resonance', S,  0.707,  12,    0,     2.91,  0.707],
        ['channel_filter_slope',     C,  0,      1,     1,     0,     0],
        ['drive_amount',             L,  0,      100,   0.1,   0,     0],
        ['tape_run',                 B,  0,      1,     1,     0,     1],
        ['tape_stop_time',           S,  10,     4000,  0,     400,   800],
        ['tape_start_time',          S,  10,     4000,  0,     400,   600],
        ['channel_fader_db',         FD, -80,    0,     0,     0,     0],
        ['fx_cut_low',               B,  0,      1,     1,     0,     0],
        ['fx_cut_mid',               B,  0,      1,     1,     0,     0],
        ['fx_cut_high',              B,  0,      1,     1,     0,     0],
        ['color_fx_type',            C,  0,      4,     1,     0,     0],
        ['color_amount',             L,  -1,     1,     0.001, 0,     0],
        ['color_parameter',          L,  0,      1,     0.001, 0,     0.5],
        ['beat_router_position',     L,  0,      1,     0.001, 0,     0],
        ['slot1_fx_type',            C,  0,      9,     1,     0,     0],
        ['slot1_enabled',            B,  0,      1,     1,     0,     1],
        ['slot1_time_source',        C,  0,      1,     1,     0,     0],
        ['slot1_beat_division',      C,  0,      10,    1,     0,     2],
        ['slot1_time_ms',            S,  1,      32000, 0,     178.9, 500],
        ['slot1_depth',              L,  0,      1,     0.001, 0,     0],
        ['slot1_decay',              L,  0,      100,   0.1,   0,     38],
        ['output_mode',              C,  0,      2,     1,     0,     1],
        ['tempo_source',             C,  0,      1,     1,     0,     0],
        ['manual_bpm',               L,  20,     300,   0.01,  0,     120],
        ['noise_resonance',          L,  0,      1,     0.001, 0,     0]
    ].map (([id, kind, start, end, interval, centre, def]) =>
        ({ id, kind, start: f (start), end: f (end), interval: f (interval), centre: f (centre), def: f (def) }));

    const BY_ID = {};
    for (const s of SPECS) BY_ID[s.id] = s;

    const skewFor = (s) => f (logf (0.5) / logf (f (f (s.centre - s.start) / f (s.end - s.start))));

    function rangeFrom0to1 (s, p)
    {
        p = jlimit (0, 1, f (p));
        switch (s.kind)
        {
            case K:  return killTaperToDb (p, s.start);
            case T:  return trimTaperToDb (p);
            case FD: return faderTaperToDb (p);
            case S:
            {
                const skew = skewFor (s);
                if (skew !== 1 && p > 0) p = expf (f (logf (p) / skew));
                return f (s.start + f (f (s.end - s.start) * p));
            }
            case C:  return jlimit (0, s.end, f (p * s.end));
            default: return f (s.start + f (f (s.end - s.start) * p));
        }
    }

    function rangeTo0to1 (s, v)
    {
        v = f (v);
        switch (s.kind)
        {
            case K:  return jlimit (0, 1, dbToKillTaper (v, s.start));
            case T:  return jlimit (0, 1, dbToTrimTaper (v));
            case FD: return jlimit (0, 1, dbToFaderTaper (v));
            case S:
            {
                const p = jlimit (0, 1, f (f (v - s.start) / f (s.end - s.start)));
                const skew = skewFor (s);
                return skew === 1 ? p : powf (p, skew);
            }
            case C:  return jlimit (0, 1, f (v / s.end));
            default: return jlimit (0, 1, f (f (v - s.start) / f (s.end - s.start)));
        }
    }

    function roundToInt (x)
    {
        // juce::roundToInt: the 1.5 * 2^52 trick, which rounds an exact half to even.
        const fl = Math.floor (x), d = x - fl;
        if (d < 0.5) return fl;
        if (d > 0.5) return fl + 1;
        return fl % 2 === 0 ? fl : fl + 1;
    }

    function rangeSnap (s, v)
    {
        v = f (v);
        if (s.kind === K || s.kind === T || s.kind === FD) return jlimit (s.start, s.end, v);
        if (s.kind === C) return roundToInt (jlimit (s.start, s.end, v));
        if (s.interval > 0) v = f (s.start + f (s.interval * Math.floor (f (f (f (v - s.start) / s.interval) + 0.5))));
        return (v <= s.start || s.end <= s.start) ? s.start : (v >= s.end ? s.end : v);
    }

    const paramFrom0to1 = (s, n) => rangeSnap (s, rangeFrom0to1 (s, jlimit (0, 1, f (n))));
    const paramTo0to1 = (s, v) => rangeTo0to1 (s, rangeSnap (s, v));
    const defaultNorm = (id) => paramTo0to1 (BY_ID[id], BY_ID[id].def);

    /** What the plugin stores for a knob at n: { norm (the knob's position), value (units) }. */
    function store (id, n)
    {
        const s = BY_ID[id];
        const norm = s.kind === B ? f (n) : paramTo0to1 (s, paramFrom0to1 (s, n));
        return { norm, value: paramFrom0to1 (s, norm) };
    }

    const valueToNorm = (id, v) => store (id, paramTo0to1 (BY_ID[id], v)).norm;

    // ---- juce::String (float, places): printf "%.*f", which rounds an exact tie to even ----

    function fixed (x, places)
    {
        x = Number (x);
        if (! isFinite (x)) return String (x);
        const neg = x < 0 || Object.is (x, -0);
        const exact = Math.abs (x).toFixed (100);          // the double's exact decimal expansion
        const [ip, fp] = exact.split ('.');
        let digits = ip + fp.slice (0, places);
        const rest = fp.slice (places);
        const first = rest.charCodeAt (0) - 48;
        const beyond = /[1-9]/.test (rest.slice (1));
        const last = digits.charCodeAt (digits.length - 1) - 48;
        const up = first > 5 || (first === 5 && (beyond || last % 2 === 1));
        if (up)
        {
            const a = digits.split ('').map (Number);
            let i = a.length - 1;
            while (i >= 0) { if (a[i] === 9) { a[i] = 0; --i; } else { a[i] += 1; break; } }
            if (i < 0) a.unshift (1);
            digits = a.join ('');
        }
        const intLen = digits.length - places;
        let out = places > 0 ? digits.slice (0, intLen) + '.' + digits.slice (intLen) : digits;
        out = out.replace (/^0+(?=\d)/, '');
        return (neg ? '-' : '') + out;
    }

    // ---- Parameters.cpp toText -----------------------------------------------------------

    function dbText (v)
    {
        if (v <= f (-79.99)) return '-inf dB';
        if (Math.abs (v) < f (0.05)) v = 0;
        return fixed (v, 1) + ' dB';
    }

    const msText = (v) => roundToInt (v) + ' ms';

    const TEXT =
    {
        trim_gain_db: dbText, eq_low_gain_db: dbText, eq_mid_gain_db: dbText, eq_high_gain_db: dbText,
        channel_fader_db: dbText,
        channel_filter_amount: (v) =>
        {
            if (Math.abs (v) <= kDeadZone) return 'Off';
            const hz = channelFilterCutoff (deadZoneAmount (v));
            return (v < 0 ? 'LPF ' : 'HPF ') + roundToInt (hz) + ' Hz';
        },
        channel_filter_resonance: (v) => 'Q ' + fixed (v, 2),
        channel_filter_slope: (v) => ['12 dB/oct', '24 dB/oct'][v],
        drive_amount: (v) => v <= 0 ? 'Off' : fixed (v, 1) + '%',
        plugin_bypass: (v) => v > 0.5 ? 'On' : 'Off',
        tape_run: (v) => v > 0.5 ? 'On' : 'Off',
        tape_stop_time: msText, tape_start_time: msText,
        fx_cut_low: (v) => v > 0.5 ? 'On' : 'Off', fx_cut_mid: (v) => v > 0.5 ? 'On' : 'Off', fx_cut_high: (v) => v > 0.5 ? 'On' : 'Off',
        color_fx_type: (v) => ['SPACE', 'DUB ECHO', 'SWEEP', 'NOISE', 'CRUSH'][v],
        color_amount: (v) =>
        {
            if (Math.abs (v) <= kDeadZone) return 'Off';
            return (v < 0 ? 'L ' : 'R ') + roundToInt (f (Math.abs (deadZoneAmount (v)) * 100)) + '%';
        },
        beat_router_position: (v) => v <= 0 ? 'SERIAL' : v >= 1 ? 'PARALLEL' : roundToInt (f (v * 100)) + '%',
        slot1_fx_type: (v) => BEAT_FX[v],
        slot1_enabled: (v) => v > 0.5 ? 'On' : 'Off',
        slot1_time_source: (v) => ['BEAT', 'FREE'][v],
        slot1_beat_division: (v) => DIVISIONS[v],
        slot1_time_ms: (v) => fixed (v, 1) + ' ms',
        slot1_decay: (v) => roundToInt (v) + '%',
        output_mode: (v) => ['OFF', 'LIMIT', 'CLIP'][v],
        tempo_source: (v) => ['HOST', 'MANUAL'][v],
        manual_bpm: (v) => fixed (v, 2) + ' BPM',
        noise_resonance: (v) => 'Q ' + fixed (0.5 * Math.pow (24.0, v), 2)
    };

    const BEAT_FX = ['DELAY', 'ECHO', 'PING PONG', 'REVERB', 'BOG THROW', 'FILTER', 'FLANGER', 'PHASER', 'ROLL', 'SPIRAL'];
    const DIVISIONS = ['1/16', '1/8', '1/4', '1/2', '1', '2', '4', '8', '16', '32', '64'];
    const COLOR_FX = ['space', 'dub', 'sweep', 'noise', 'crush'];

    // No toText: JUCE's default prints as many places as the interval has (0.001: three).
    const paramText = (id, value) => TEXT[id] ? TEXT[id] (value) : fixed (value, 3);

    // ---- PlatePanel::knobValueText / knobLabelText ---------------------------------------------

    /*  st: { norm (id -> knob position), value (id -> units), colorOff }. The Color FX index is -1
        while Color FX is off. */
    function colorFxIndex (st) { return st.colorOff ? -1 : roundToInt (st.value.color_fx_type); }
    const colorIsUnipolar = (st) => { const fx = colorFxIndex (st); return fx === 3 || fx === 4; };
    const bogThrowChosen = (st) => roundToInt (st.value.slot1_fx_type) === 4;

    const SIDES = [['mid', 'hi'], ['mid', 'hi'], ['gate', 'band'], ['LP', 'HP'], ['drive', 'bits']];

    function knobValueText (id, st)
    {
        if (id === 'color_amount')
        {
            if (colorIsUnipolar (st))
            {
                const m = st.norm.color_amount;
                return m < f (0.005) ? 'Off' : roundToInt (f (m * 100)) + '%';
            }
            const v = st.value.color_amount;
            if (Math.abs (v) <= kDeadZone) return 'Off';
            const fx = colorFxIndex (st);
            const side = fx < 0 ? (v < 0 ? 'low' : 'hi') : SIDES[fx][v < 0 ? 0 : 1];
            return side + ' ' + roundToInt (f (Math.abs (v) * 100));
        }

        if (id === 'color_parameter')
        {
            const v = st.norm.color_parameter;
            switch (colorFxIndex (st))
            {
                case 0: { const s = colorSpaceDecaySeconds (v); return fixed (s, s < 1 ? 2 : 1) + ' s'; }
                case 1: return roundToInt (f (colorDubEchoFeedback (v) * 100)) + ' %';
                case 2: return roundToInt (f (v * 100)) + ' %';
                case 3: return v <= 0 ? 'Off' : roundToInt (jmapf (v, -80, -6)) + ' dB';
                case 4: return roundToInt (colorCrushBitDepth (v)) + ' bit';
                default: return fixed (v, 3);
            }
        }

        if (bogThrowChosen (st))
        {
            if (id === 'slot1_depth') return String (roundToInt (bogThrowFromAmount (st.norm.slot1_depth)));
            if (id === 'slot1_decay')
            {
                const s = bogDecaySecondsFromDecay (f (st.value.slot1_decay * f (0.01)));
                return fixed (s, s < 10 ? 2 : 1) + ' s';
            }
        }

        if (id === 'slot1_depth') return fixed (st.norm.slot1_depth, 3);
        if (id === 'slot1_decay') return fixed (f (st.value.slot1_decay * f (0.01)), 3);

        return paramText (id, st.value[id]);
    }

    function knobLabelText (id, st, fallback)
    {
        if (id === 'color_parameter')
        {
            const fx = colorFxIndex (st);
            return fx < 0 ? 'parameter' : ['decay', 'feedback', 'shape', 'level', 'bits'][fx];
        }
        if (id === 'slot1_depth' && bogThrowChosen (st)) return 'throw';
        return fallback;
    }

    /** PlatePanel::knobDefault: COLOR's Off is the bottom on NOISE and CRUSH, the centre elsewhere. */
    function knobDefault (id, st)
    {
        if (id === 'color_amount' && colorIsUnipolar (st)) return 0;
        return defaultNorm (id);
    }

    /** The gain reduction figure: "0.0 dB" under 0.05, else a typographic minus and one place. */
    const grText = (held) => (held < f (0.05) ? '0.0' : '−' + fixed (held, 1)) + ' dB';

    const api =
    {
        SPECS, BY_ID, BEAT_FX, DIVISIONS, COLOR_FX,
        store, valueToNorm, defaultNorm, paramFrom0to1, paramTo0to1,
        paramText, knobValueText, knobLabelText, knobDefault, grText, fixed, roundToInt,
        colorFxIndex, colorIsUnipolar, bogThrowChosen, beatFxUsesTime: (t) => t !== 4,
        beatFxHasDecay: (t) => t !== 5 && t !== 6 && t !== 7 && t !== 8,
        maxBeatDivisionIndex: (t) => (t === 5 || t === 6 || t === 7) ? 10 : 8
    };

    if (typeof module === 'object' && module.exports) module.exports = api;
    else root.CwlParams = api;
}) (typeof window !== 'undefined' ? window : globalThis);

;
/*  CWL DJ Mixer browser demo: the plate.

    window.CwlPanel.mount (root, mixer)

    The drawing is the 2.2 plate mock's (claude-input/design-references/cwl-panel-2.2-plate.html,
    copied beside this file): knobGloss, ticksSvg, screwSvg, the fader ladder, the meter, the gear
    and the colour dial are its functions, unchanged but for scoping. The gestures are the mock's
    and the plugin's (PlatePanel.cpp): knobs drag vertically, 150 px for the whole travel, Shift for
    a quarter of the rate, a detent at the default that catches within 5 px and lets go after 12,
    double click to the default; a slot jumps to the click and drags; the fader drags; keys press.

    What the mock only pretended, the plugin does, and so does this: every control is a parameter.
    A knob's position is the plugin's normalised value and goes to the engine as it is
    (mixer.setParamNormalised); its read-out is the plugin's own string (CwlParams, checked against
    the plugin's getText by web/gates/readouts.mjs). The meter, the gain reduction, the BPM glass and
    the scope read the running strip (mixer.telemetry). The mock's design review tools, its fake
    meter and its fake spectrum are not here.
*/

(function ()
{
    'use strict';

    const P = window.CwlParams;

    // ---- the mock's drawing ------------------------------------------------------------------

    const A0 = -135, A1 = 135;
    const pol = (cx, cy, r, deg) => { const a = (deg - 90) * Math.PI / 180; return [cx + r * Math.cos (a), cy + r * Math.sin (a)]; };
    const arc = (cx, cy, r, f, t) => { const [x0, y0] = pol (cx, cy, r, f), [x1, y1] = pol (cx, cy, r, t); return `M ${x0} ${y0} A ${r} ${r} 0 ${(t - f) > 180 ? 1 : 0} 1 ${x1} ${y1}`; };
    let UID = 0;
    const shade = (hex, k) => { const n = parseInt (hex.slice (1), 16); const r = Math.min (255, Math.round (((n >> 16) & 255) * k)), g = Math.min (255, Math.round (((n >> 8) & 255) * k)), bb = Math.min (255, Math.round ((n & 255) * k)); return '#' + ((r << 16) | (g << 8) | bb).toString (16).padStart (6, '0'); };
    const snap = (x) => Math.round (x * 2) / 2;
    const grip = (c, deg, rIn, rOut, w, fill, stroke, rx) => `<rect x="${(-w / 2).toFixed (2)}" y="${(-rOut).toFixed (2)}" width="${w.toFixed (2)}" height="${(rOut - rIn).toFixed (2)}" rx="${rx}" fill="${fill}" stroke="${stroke}" stroke-width=".6" transform="translate(${c} ${c}) rotate(${deg})"/>`;
    const KNOB3D = true;   // the mock's depth pass, on: 1.7.1 onwards

    function knobGloss (cap, v, bip, def)
    {
        const id = 'g' + (++UID), c = cap / 2;
        const dish = snap (cap * 0.47), r = snap (cap * 0.40), gw = cap * 0.10, aw = cap * 0.075;
        const D = KNOB3D;
        const side = snap (cap * 0.32), f = snap (cap * 0.235), ex = D ? (cap >= 52 ? 5 : 3) : (cap >= 52 ? 3 : 2), n = 18;
        const pitch = Math.PI * 2 * (side + f) / 2 / n;
        const ang = A0 + (A1 - A0) * v; const from = bip ? 0 : A0; let lo = Math.min (from, ang), hi = Math.max (from, ang);
        const idle = (hi - lo) < 1.5;
        if (hi - lo < 8) { const m = (lo + hi) / 2; lo = Math.max (A0, m - 4); hi = Math.min (A1, m + 4); }
        const [px0, py0] = pol (c, c, cap * 0.05, ang), [px1, py1] = pol (c, c, f * 0.88, ang);
        const pw = Math.max (1.5, cap * 0.04);
        const dAng = bip ? 0 : (A0 + (A1 - A0) * (def === undefined ? 0 : def));
        const [dx0, dy0] = pol (c, c, dish - 0.8, dAng), [dx1, dy1] = pol (c, c, dish - 3.2, dAng);
        let grips = '';
        for (let i = 0; i < n; i++) { const d = i * 360 / n; grips += `<g transform="translate(0 ${ex})">${grip (c, d, f - 0.5, side, pitch * 0.58, shade ('#484c52', 0.45), '#000', 1.2)}</g>`; }
        for (let i = 0; i < n; i++) { const d = i * 360 / n; grips += grip (c, d, f - 0.5, side, pitch * 0.58, shade ('#484c52', 1 + (D ? 0.62 : 0.45) * Math.cos ((d + 20) * Math.PI / 180)), '#0d0e10', 1.2); }
        return `<svg width="${cap}" height="${cap}" viewBox="0 0 ${cap} ${cap}">
      <defs>
        <radialGradient id="${id}dish" cx="50%" cy="50%" r="50%"><stop offset="72%" stop-color="#17191c"/><stop offset="100%" stop-color="#0b0c0e"/></radialGradient>
        <radialGradient id="${id}fc" cx="${D ? '42%' : '50%'}" cy="${D ? '34%' : '38%'}" r="${D ? '66%' : '60%'}"><stop offset="0" stop-color="${D ? '#353a40' : '#2b2e33'}"/><stop offset="100%" stop-color="${D ? '#111316' : '#17191c'}"/></radialGradient>
        <linearGradient id="${id}wall" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#26292e"/><stop offset="100%" stop-color="#050506"/></linearGradient>
        <radialGradient id="${id}ao" cx="50%" cy="52%" r="50%">
          <stop offset="${(side / (side + 4) * 100).toFixed (1)}%" stop-color="rgba(0,0,0,.85)"/>
          <stop offset="100%" stop-color="rgba(0,0,0,0)"/>
        </radialGradient>
        <linearGradient id="${id}arc" x1="0" y1="0" x2="0" y2="1"><stop class="arcA" offset="0" stop-color="#ffa03d"/><stop class="arcB" offset="100%" stop-color="#f0780a"/></linearGradient>
        <filter id="${id}bl" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="${(cap * 0.06).toFixed (2)}"/></filter>
        <filter id="${id}grain" x="0" y="0" width="1" height="1"><feTurbulence type="fractalNoise" baseFrequency="1.1" numOctaves="2" seed="${UID}"/><feColorMatrix values="0 0 0 0 .55 0 0 0 0 .55 0 0 0 0 .57 0 0 0 .14 0"/></filter>
      </defs>
      <circle cx="${c}" cy="${c}" r="${dish}" fill="url(#${id}dish)" stroke="#000" stroke-width="1"/>
      <path d="${arc (c, c, dish - 0.6, 100, 260)}" fill="none" stroke="rgba(255,255,255,.12)" stroke-width="1"/>
      <path d="${arc (c, c, r, A0, A1)}" fill="none" stroke="#07080a" stroke-width="${gw}" stroke-linecap="round"/>
      <path class="arcv" d="${arc (c, c, r, lo, hi)}" fill="none" stroke="${idle ? '#33373c' : 'url(#' + id + 'arc)'}" stroke-width="${aw}"/>
      <line x1="${dx0}" y1="${dy0}" x2="${dx1}" y2="${dy1}" stroke="rgba(255,255,255,.38)" stroke-width="1.2" stroke-linecap="round"/>
      <circle class="cast" cx="${(c + cap * (D ? 0.04 : 0.03)).toFixed (2)}" cy="${(c + ex + cap * (D ? 0.08 : 0.06)).toFixed (2)}" r="${side + (D ? 4 : 3)}" fill="rgba(0,0,0,${D ? '.8' : '.7'})" filter="url(#${id}bl)"/>
      <circle cx="${c}" cy="${c + ex}" r="${side + 4}" fill="url(#${id}ao)"/>
      <circle cx="${c}" cy="${c + ex}" r="${side + 1}" fill="#000"/><circle cx="${c}" cy="${c + ex}" r="${side}" fill="${D ? 'url(#' + id + 'wall)' : '#0f1113'}"/>
      <g class="capg">
      <circle cx="${c}" cy="${c}" r="${side + 1}" fill="#000"/><circle cx="${c}" cy="${c}" r="${side}" fill="#1a1c20"/>
      ${grips}
      <circle cx="${c}" cy="${c}" r="${f + 0.8}" fill="#0a0b0c"/>
      <circle cx="${c}" cy="${c}" r="${f}" fill="url(#${id}fc)"/>
      <circle cx="${c}" cy="${c}" r="${f}" filter="url(#${id}grain)"/>
      <path d="${arc (c, c, f - 0.6, -100, 100)}" fill="none" stroke="rgba(255,255,255,.28)" stroke-width="1"/>
      <path class="hl" d="${arc (c, c, side + 0.5, -105, 15)}" fill="none" stroke="rgba(255,255,255,${D ? '.42' : '.20'})" stroke-width="${D ? 1.4 : 1}"/>
      ${D ? `<path d="${arc (c, c, f - 0.9, 110, 250)}" fill="none" stroke="rgba(0,0,0,.45)" stroke-width="1.6"/>` : ''}
      <ellipse class="hl" cx="${(c - f * 0.22).toFixed (2)}" cy="${(c - f * 0.38).toFixed (2)}" rx="${(f * 0.5).toFixed (2)}" ry="${(f * 0.26).toFixed (2)}" fill="rgba(255,255,255,.06)"/>
      <line class="ptrs" x1="${px0}" y1="${py0 + 0.9}" x2="${px1}" y2="${py1 + 0.9}" stroke="rgba(0,0,0,.7)" stroke-width="${pw + 0.6}" stroke-linecap="round"/>
      <line class="ptr" x1="${px0}" y1="${py0}" x2="${px1}" y2="${py1}" stroke="#eef0f2" stroke-width="${pw}" stroke-linecap="round"/>
      </g>
    </svg>`;
    }

    /* the end labels printed at the stops; COLOR's and PARAMETER's follow the chosen effect */
    const ENDS = { low: ['-26', '+6'], mid: ['-26', '+6'], hi: ['-26', '+6'], filter: ['LPF', 'HPF'], color: ['LOW', 'HI'], parameter: ['MIN', 'MAX'] };

    function ticksSvg (cap, bip, lab)
    {
        const pad = Math.round (cap * 0.13), S = cap + 2 * pad, c = S / 2, r0 = cap * 0.47 + 2.5, n = 11; let t = '';
        const e = ENDS[(lab || '').toLowerCase()];
        if (e)
        {
            const [lx, ly] = pol (c, c, r0 + 6, A0), [rx, ry] = pol (c, c, r0 + 6, A1);
            t += `<text class="end" x="${(lx + 1).toFixed (1)}" y="${(ly + 5).toFixed (1)}" text-anchor="middle">${e[0]}</text><text class="end" x="${(rx - 1).toFixed (1)}" y="${(ry + 5).toFixed (1)}" text-anchor="middle">${e[1]}</text>`;
        }
        for (let i = 0; i < n; i++)
        {
            const d = A0 + (A1 - A0) * i / (n - 1), big = (i === 0 || i === n - 1 || i === (n - 1) / 2), Ln = big ? 4 : 2.5;
            const [x0, y0] = pol (c, c, r0, d), [x1, y1] = pol (c, c, r0 + Ln, d);
            t += `<line x1="${x0.toFixed (2)}" y1="${y0.toFixed (2)}" x2="${x1.toFixed (2)}" y2="${y1.toFixed (2)}" stroke-width="${big ? 1.2 : 1}"/>`;
        }
        return `<svg class="ticks" width="${S}" height="${S}" viewBox="0 0 ${S} ${S}" style="top:${-pad}px;left:50%;margin-left:${-c}px">${t}</svg>`;
    }

    function screwSvg (rot)
    {
        const id = 's' + (++UID), c = 6.5;
        return `<svg width="13" height="13" viewBox="0 0 13 13">
      <defs>
        <radialGradient id="${id}h" cx="38%" cy="32%" r="70%">
          <stop offset="0" style="stop-color:var(--sc1,#d9dde1)"/><stop offset="35%" style="stop-color:var(--sc2,#9aa0a6)"/>
          <stop offset="75%" style="stop-color:var(--sc3,#5b6167)"/><stop offset="100%" style="stop-color:var(--sc4,#2f3337)"/>
        </radialGradient>
        <linearGradient id="${id}sink" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#050607"/><stop offset="100%" stop-color="#3a3e43"/></linearGradient>
        <linearGradient id="${id}slot" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0a0b0c"/><stop offset="70%" stop-color="#141618"/><stop offset="100%" stop-color="#6a7076"/></linearGradient>
      </defs>
      <circle cx="${c}" cy="${c}" r="6.4" fill="url(#${id}sink)"/>
      <circle cx="${c}" cy="${c}" r="5.6" fill="#000"/>
      <circle cx="${c}" cy="${c}" r="5.1" fill="url(#${id}h)"/>
      <circle cx="${c}" cy="${c}" r="4.7" fill="none" stroke="rgba(255,255,255,.28)" stroke-width=".6" stroke-dasharray="9 21" transform="rotate(-140 ${c} ${c})"/>
      <g transform="rotate(${rot} ${c} ${c})">
        <rect x="${c - 3.3}" y="${c - 0.9}" width="6.6" height="1.8" rx=".5" fill="url(#${id}slot)"/>
        <rect x="${c - 0.9}" y="${c - 3.3}" width="1.8" height="6.6" rx=".5" fill="url(#${id}slot)"/>
        <rect x="${c - 1.3}" y="${c - 1.3}" width="2.6" height="2.6" rx=".6" fill="#0b0c0d"/>
      </g>
    </svg>`;
    }

    /* the glass colour: the mock's six stops and its HSL blend */
    const STOPS = ['#1a63c8', '#2a93a3', '#4ab36a', '#f9a900', '#f06f1a', '#f23a3c'];
    const hex = (h) => [1, 3, 5].map ((i) => parseInt (h.substr (i, 2), 16));
    const toHsl = ([r, g, b]) => { r /= 255; g /= 255; b /= 255; const mx = Math.max (r, g, b), mn = Math.min (r, g, b), l = (mx + mn) / 2, d = mx - mn; if (! d) return [0, 0, l]; const s = d / (1 - Math.abs (2 * l - 1)); const h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4; return [((h * 60) + 360) % 360, s, l]; };
    const toRgb = ([h, s, l]) => { const c = (1 - Math.abs (2 * l - 1)) * s, x = c * (1 - Math.abs ((h / 60) % 2 - 1)), m = l - c / 2; const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x]; return [r, g, b].map ((v) => Math.round ((v + m) * 255)); };
    const mixHsl = (a, b, t) => { const A = toHsl (a), B = toHsl (b); let dh = B[0] - A[0]; if (dh > 180) dh -= 360; if (dh < -180) dh += 360; return toRgb ([((A[0] + dh * t) + 360) % 360, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t]); };
    const lift = (c, k) => c.map ((v) => Math.round (v + (255 - v) * k)), sink = (c, k) => c.map ((v) => Math.round (v * (1 - k)));
    const clamp01 = (x) => Math.max (0, Math.min (1, x));

    // ---- the skins ------------------------------------------------------------------------------

    const SKINS = [['skin-dark', 'Dark'], ['skin-carbon', 'Carbon'], ['skin-clear', 'Clear'], ['skin-book', 'Coloring book'],
                   ['skin-blackout', 'Blackout'], ['skin-gold', 'Gold'], ['skin-light', 'Light'], ['skin-blue', 'Baby blue'],
                   ['skin-camo', 'Camo'], ['skin-army', 'Army'], ['skin-pink', 'Pink']];
    const FAMILY = { 'skin-gold': 'skin-light', 'skin-book': 'skin-light' };   // a skin that borrows the light family's ink and key rules
    const LIGHT = ['skin-light', 'skin-blue', 'skin-pink', 'skin-gold', 'skin-book'];
    const VIG = 'inset 0 0 70px rgba(0,0,0,.55), inset 0 0 22px rgba(0,0,0,.35)';
    const VIG_PAPER = 'inset 0 0 70px rgba(0,0,0,.40), inset 0 0 22px rgba(0,0,0,.24)';
    const VIG_GOLD = 'inset 0 0 80px rgba(0,0,0,.50), inset 0 0 24px rgba(0,0,0,.30)';
    const VIG_LIGHT = 'inset 0 0 80px rgba(0,0,0,.34), inset 0 0 24px rgba(0,0,0,.18)';

    const store = (k, v) => { try { if (v === undefined) return localStorage.getItem (k); localStorage.setItem (k, v); } catch (e) {} return null; };

    // ---- the panel ------------------------------------------------------------------------------

    function mount (root, mixer)
    {
        const plate = root.querySelector ('.plate');
        const $ = (q) => plate.querySelector (q), $$ = (q) => Array.from (plate.querySelectorAll (q));

        // -- state: what the plugin holds, in its own units ----------------------------------
        const st = { norm: {}, value: {}, colorOff: false };
        for (const s of P.SPECS) { st.norm[s.id] = P.defaultNorm (s.id); st.value[s.id] = P.paramFrom0to1 (s, st.norm[s.id]); }

        /* BOG THROW is out of the browser demo. The panel still shows it so people can see the
           effect exists, but the mixer never receives the type, so it never runs the effect and
           never fetches the 4.26 MB reverb bank. The plugin itself is untouched. */
        const isBog = () => P.roundToInt (st.value.slot1_fx_type) === 4;

        function set (id, n, send = true)
        {
            const wasBog = isBog();
            const r = P.store (id, n);
            st.norm[id] = r.norm; st.value[id] = r.value;
            if (send && mixer)
            {
                if (isBog())
                {
                    // Parked: hold the type and the ON key back, and mute the slot on arrival.
                    if (id !== 'slot1_fx_type' && id !== 'slot1_enabled') mixer.setParamNormalised (id, n);
                    if (! wasBog) mixer.setParamNormalised ('slot1_enabled', 0);
                }
                else
                {
                    mixer.setParamNormalised (id, n);
                    // Leaving BOG THROW: hand the mixer back the type and the ON key it never saw.
                    if (wasBog)
                    {
                        mixer.setParamNormalised ('slot1_fx_type', st.norm.slot1_fx_type);
                        mixer.setParamNormalised ('slot1_enabled', st.norm.slot1_enabled);
                    }
                }
            }
            refresh();
        }

        const index = (id) => P.roundToInt (st.value[id]);
        const on = (id) => st.value[id] > 0.5;

        // -- build: the mock's knobs, screws, meter, ladder and gear ------------------------------
        const knobDef = (el) => P.knobDefault (el.dataset.param, st);
        const isBipolar = (el) => el.dataset.param === 'channel_filter_amount' || (el.dataset.param === 'color_amount' && ! P.colorIsUnipolar (st));

        function buildKnob (el)
        {
            const cap = parseInt (el.dataset.cap, 10);
            const bip = isBipolar (el);
            el.dataset.bipolar = bip ? '1' : '0';
            const lab = el.dataset.lab;
            el.innerHTML = (lab ? ticksSvg (cap, bip, el.dataset.role || lab) : '') + knobGloss (cap, st.norm[el.dataset.param], bip, knobDef (el))
                         + (lab ? `<div class="kval"></div><div class="klab">${lab}</div>` : '');
        }

        $$('.knob[data-param]').forEach (buildKnob);
        [['tl', 12], ['tr', -31], ['bl', 58], ['br', -8]].forEach (([k, r]) => { const el = $('.scr.' + k); if (el) el.innerHTML = screwSvg (r); });

        const bars = $('.bars');
        for (let i = 0; i < 20; i++) bars.appendChild (document.createElement ('s'));
        const grm = $('.grm');
        for (let i = 0; i < 12; i++) grm.appendChild (document.createElement ('s'));

        {
            const g = $('.gear2'), n = 8, R = 8, r = 6.2, hub = 2.4, c = 8; let d = '';
            for (let i = 0; i < n; i++)
            {
                const a0 = i * Math.PI * 2 / n, w = Math.PI * 2 / n;
                [[r, a0 - w * .22], [R, a0 - w * .15], [R, a0 + w * .15], [r, a0 + w * .22], [r, a0 + w * .5]].forEach (([rr, a], j) =>
                { d += (i === 0 && j === 0 ? 'M' : 'L') + (c + rr * Math.cos (a)).toFixed (2) + ' ' + (c + rr * Math.sin (a)).toFixed (2) + ' '; });
            }
            d += 'Z M' + (c + hub) + ' ' + c + ' a' + hub + ' ' + hub + ' 0 1 0 ' + (-2 * hub) + ' 0 a' + hub + ' ' + hub + ' 0 1 0 ' + (2 * hub) + ' 0 Z';
            if (g) g.innerHTML = '<svg viewBox="0 0 16 16"><path d="' + d + '" fill-rule="evenodd"/></svg>';
        }

        function buildLadder ()
        {
            const fs = $('.fslot'); const old = fs.querySelector ('.fticks'); if (old) old.remove();
            const W = fs.offsetWidth || 44, H = fs.offsetHeight || 100, y0 = 15, y1 = H - 15, n = 11, m = W / 2; let t = '';
            for (let i = 0; i < n; i++)
            {
                const y = (y0 + (y1 - y0) * i / (n - 1)).toFixed (1);
                t += `<line x1="2" y1="${y}" x2="${m - 3}" y2="${y}" stroke-width="1"/><line x1="${m + 3}" y1="${y}" x2="${W - 2}" y2="${y}" stroke-width="1"/>`;
                t += `<text x="${W + 2}" y="${(+y + 2).toFixed (1)}" font-size="5.5" font-family="Poppins,sans-serif" font-weight="600">${10 - i}</text>`;
            }
            fs.insertAdjacentHTML ('beforeend', `<svg class="fticks" width="${W + 12}" height="${H}" viewBox="0 0 ${W + 12} ${H}">${t}</svg>`);
        }
        buildLadder();

        function placeScale ()
        {
            const lv = $('.lv'); if (! lv) return;
            const leds = lv.querySelectorAll ('.bars s'), sc = lv.querySelector ('.scale'), sr = sc.getBoundingClientRect();
            const z = parseFloat (getComputedStyle (plate).zoom) || 1;
            sc.querySelectorAll ('.dj').forEach ((t) =>
            {
                const i = parseInt (t.dataset.i, 10); const l = leds[i === 0 ? 0 : i - 1]; if (! l) return; const r = l.getBoundingClientRect();
                const y = i === 0 ? (r.bottom - sr.top) / z + 2 : (r.top + r.height / 2 - sr.top) / z - 3.5; t.style.top = Math.round (y) + 'px';
            });
        }

        // -- paint: every control from the state --------------------------------------------------

        function paintKnob (el)
        {
            const id = el.dataset.param, v = st.norm[id];
            const cap = parseInt (el.dataset.cap, 10), bip = el.dataset.bipolar === '1', def = knobDef (el);
            el.classList.toggle ('atdef', Math.abs (v - def) < 0.005);
            const svg = el.querySelector ('svg:not(.ticks)');
            const arcv = svg && svg.querySelector ('.arcv'), ptr = svg && svg.querySelector ('.ptr'), ptrs = svg && svg.querySelector ('.ptrs');
            if (arcv && ptr && ptrs)
            {
                const c = cap / 2, r = snap (cap * 0.40), f = snap (cap * 0.235);
                const ang = A0 + (A1 - A0) * v; const from = bip ? 0 : A0; let lo = Math.min (from, ang), hi = Math.max (from, ang);
                const idle = (hi - lo) < 1.5; if (hi - lo < 8) { const m = (lo + hi) / 2; lo = Math.max (A0, m - 4); hi = Math.min (A1, m + 4); }
                arcv.setAttribute ('d', arc (c, c, r, lo, hi)); arcv.setAttribute ('stroke', idle ? '#33373c' : 'url(#' + svg.querySelector ('linearGradient[id$="arc"]').id + ')');
                const [px0, py0] = pol (c, c, cap * 0.05, ang), [px1, py1] = pol (c, c, f * 0.88, ang);
                ptr.setAttribute ('x1', px0); ptr.setAttribute ('y1', py0); ptr.setAttribute ('x2', px1); ptr.setAttribute ('y2', py1);
                ptrs.setAttribute ('x1', px0); ptrs.setAttribute ('y1', py0 + 0.9); ptrs.setAttribute ('x2', px1); ptrs.setAttribute ('y2', py1 + 0.9);
            }
            const kv = el.querySelector ('.kval'); if (kv) setReadout (kv, P.knobValueText (id, st));
            const kl = el.querySelector ('.klab'); if (kl) kl.textContent = P.knobLabelText (id, st, el.dataset.lab);
        }

        /* the mock's unit rule: a unit hangs off the number smaller, Q leads */
        const RX = /^(Q )?(-?[\d.]+)( ?)(dB|ms|s|%|bit)?$/;
        function setReadout (el, text)
        {
            if (el.dataset.text === text) return;
            el.dataset.text = text;
            const t = text.replace (/−/g, '-').trim(), m = t.match (RX);
            if (! m || (! m[1] && ! m[4])) { el.textContent = text; return; }
            const num = text.replace (/^Q /, '').replace (/ ?(dB|ms|s|%|bit)$/, '');
            el.innerHTML = (m[1] ? '<span class="u uq">Q</span>' : '') + num + (m[4] ? '<span class="u">' + m[4] + '</span>' : '');
        }

        const slotLeft = (norm) => `calc(8px + ${norm} * (100% - 16px))`;

        function paintColorEnds ()
        {
            const fx = P.colorFxIndex (st), name = fx < 0 ? '' : P.COLOR_FX[fx];
            const PE = { space: ['MID', 'HI'], dub: ['MID', 'HI'], sweep: ['GATE', 'BAND'], noise: ['OFF', 'FULL'], crush: ['OFF', 'FULL'] };
            const ck = $('.knob[data-param="color_amount"]');
            const e = PE[name] || ['LOW', 'HI'], ends = ck.querySelectorAll ('.ticks text.end');
            if (ends.length === 2) { ends[0].textContent = e[0]; ends[1].textContent = e[1]; }
            const pk = $('.knob[data-param="color_parameter"]'), lab = P.knobLabelText ('color_parameter', st, 'parameter');
            const PP = { decay: ['MIN', 'MAX'], feedback: ['MIN', 'MAX'], shape: ['MIN', 'MAX'], level: ['MIN', 'MAX'], bits: ['16', '4'] };
            const q = PP[lab] || ['MIN', 'MAX'], pe = pk.querySelectorAll ('.ticks text.end');
            if (pe.length === 2) { pe[0].textContent = q[0]; pe[1].textContent = q[1]; }
            $('.knob[data-param="noise_resonance"]').hidden = fx !== 3;
        }

        let lastBipolar = null;

        function refresh ()
        {
            // COLOR is bipolar on SPACE, DUB ECHO and SWEEP, and a riser on NOISE and CRUSH.
            const bip = ! P.colorIsUnipolar (st);
            if (bip !== lastBipolar) { lastBipolar = bip; buildKnob ($('.knob[data-param="color_amount"]')); }

            $$('.knob[data-param]').forEach (paintKnob);
            paintColorEnds();

            // keys
            $$('.key[data-param][data-value]').forEach ((k) => k.classList.toggle ('on', index (k.dataset.param) === parseInt (k.dataset.value, 10)));
            $$('.key[data-param][data-toggle]').forEach ((k) => k.classList.toggle ('on', on (k.dataset.param)));
            const onk = $('.key.onk'); onk.classList.toggle ('on', on ('slot1_enabled')); onk.classList.toggle ('live', on ('slot1_enabled'));

            // The tape key shows the action: STOP while running, START once stopped, lit while stopped.
            const tk = $('.key.tape'); const stopped = ! on ('tape_run');
            tk.textContent = stopped ? 'START' : 'STOP'; tk.classList.toggle ('on', stopped);

            // Color FX list: one lit, or none while Color FX is off.
            const cfx = P.colorFxIndex (st);
            $$('.plist div').forEach ((d, i) => d.classList.toggle ('on', i === cfx));

            // Beat FX selector and division, and what sleeps
            const type = index ('slot1_fx_type');
            $('.fxsel .selrow:not(.div) .glass.sel').textContent = P.BEAT_FX[type];
            $('.fxsel .selrow.div .glass.sel').textContent = P.DIVISIONS[index ('slot1_beat_division')];
            const beat = $('.s-beat');
            beat.classList.toggle ('bog', type === 4);
            beat.classList.toggle ('bogdemo', type === 4);   // demo only: greyed and dead
            beat.classList.toggle ('nodecay', ! P.beatFxHasDecay (type));
            beat.classList.toggle ('tmode', index ('slot1_time_source') === 1);
            beat.classList.toggle ('bogloading', type === 4 && root.classList.contains ('bank-loading'));

            // sliders and fader
            $('.s-input .slot i').style.left = slotLeft (st.norm.trim_gain_db);
            setReadout ($('.s-input .sval'), P.paramText ('trim_gain_db', st.value.trim_gain_db));
            $('.mrow .slot i').style.left = slotLeft (st.norm.slot1_time_ms);
            setReadout ($('.mrow .sval'), P.paramText ('slot1_time_ms', st.value.slot1_time_ms));
            const fs = $('.fslot'), H = fs.clientHeight || 104;
            fs.querySelector ('b').style.top = ((1 - st.norm.channel_fader_db) * (H - 26)).toFixed (1) + 'px';
            setReadout ($('.lvval'), P.paramText ('channel_fader_db', st.value.channel_fader_db));

            plate.classList.toggle ('bypassed', on ('plugin_bypass'));
            paintGlass();
        }

        // -- the BPM glass: HOST, MAN or NO HOST, and the tempo the strip is on ---------------------

        const glass = $('.top .glass');
        let shownBpm = mixer ? mixer.bpm : 120, followingHost = true;

        function paintGlass ()
        {
            const manual = index ('tempo_source') === 1;
            const sync = followingHost && ! manual ? 'HOST' : manual ? 'MAN' : 'NO HOST';
            const bpm = manual ? st.value.manual_bpm : shownBpm;
            const text = P.fixed (bpm, 1);
            if (glass.dataset.bpm !== text) { glass.firstChild.nodeValue = text + ' '; glass.dataset.bpm = text; }
            glass.querySelector ('em').textContent = sync;
            plate.classList.toggle ('nohost', sync === 'NO HOST');
        }

        // -- gestures -------------------------------------------------------------------------------

        const CATCH = 5 / 150, ARM = 7.5 / 150, RELEASE = 12, SETTLE = 6 / 150;

        plate.addEventListener ('pointerdown', (e) =>
        {
            if (e.button !== 0) return;

            const knob = e.target.closest ('.knob[data-param]');
            if (knob && e.target.closest ('svg:not(.ticks)'))
            {
                e.preventDefault();
                const id = knob.dataset.param, def = P.knobDefault (id, st);
                let y0 = e.clientY, v0 = st.norm[id], held = false, holdY = 0, lastV = v0;
                let armed = Math.abs (v0 - def) > ARM;
                knob.classList.add ('drag');
                knob.setPointerCapture && knob.setPointerCapture (e.pointerId);
                const move = (ev) =>
                {
                    const travel = ev.shiftKey ? 600 : 150;
                    if (held)
                    {
                        if (Math.abs (ev.clientY - holdY) > RELEASE) { held = false; v0 = def; y0 = ev.clientY; lastV = def; }
                        else { set (id, def); return; }
                    }
                    let v = clamp01 (v0 - (ev.clientY - y0) / travel);
                    const crossed = (lastV - def) * (v - def) < 0;
                    if (armed && (Math.abs (v - def) <= CATCH || crossed)) { held = true; armed = false; holdY = ev.clientY; v = def; }
                    else if (Math.abs (v - def) > ARM) armed = true;
                    lastV = v; set (id, v);
                };
                const up = () =>
                {
                    window.removeEventListener ('pointermove', move); window.removeEventListener ('pointerup', up); window.removeEventListener ('pointercancel', up);
                    knob.classList.remove ('drag');
                    if (Math.abs (st.norm[id] - def) <= SETTLE && st.norm[id] !== def) set (id, def);
                };
                window.addEventListener ('pointermove', move); window.addEventListener ('pointerup', up); window.addEventListener ('pointercancel', up);
                return;
            }

            const slot = e.target.closest ('.slot[data-param]');
            if (slot)
            {
                e.preventDefault();
                const id = slot.dataset.param;
                const at = (ev) => { const r = slot.getBoundingClientRect(), z = r.width / slot.offsetWidth || 1; return ((ev.clientX - r.left) / z - 8) / (slot.offsetWidth - 16); };
                set (id, clamp01 (at (e)));
                const move = (ev) => set (id, clamp01 (at (ev)));
                const up = () => { window.removeEventListener ('pointermove', move); window.removeEventListener ('pointerup', up); };
                window.addEventListener ('pointermove', move); window.addEventListener ('pointerup', up);
                return;
            }

            const fader = e.target.closest ('.fslot[data-param]');
            if (fader)
            {
                e.preventDefault();
                const id = fader.dataset.param, y0 = e.clientY, v0 = st.norm[id];
                const z = fader.getBoundingClientRect().height / fader.offsetHeight || 1;
                const travel = (fader.offsetHeight - 24 - 26) * z;
                const move = (ev) => set (id, clamp01 (v0 + (y0 - ev.clientY) / travel));
                const up = () => { window.removeEventListener ('pointermove', move); window.removeEventListener ('pointerup', up); };
                window.addEventListener ('pointermove', move); window.addEventListener ('pointerup', up);
                return;
            }

            const dial = e.target.closest ('.dial');
            if (dial)
            {
                e.preventDefault(); setDisco (false);
                const y0 = e.clientY, h0 = hue;
                const move = (ev) => { hue = clamp01 (h0 - (ev.clientY - y0) / 160); paintHue(); };
                const up = () => { window.removeEventListener ('pointermove', move); window.removeEventListener ('pointerup', up); store ('cwl-web-hue', String (hue)); };
                window.addEventListener ('pointermove', move); window.addEventListener ('pointerup', up);
            }
        });

        plate.addEventListener ('dblclick', (e) =>
        {
            const knob = e.target.closest ('.knob[data-param]');
            /* The knob takes pointer capture on drag, so the browser retargets dblclick to the
               knob div itself. An svg-only guard never matched and the reset never ran. */
            if (knob && ! e.target.closest ('svg.ticks')) set (knob.dataset.param, P.knobDefault (knob.dataset.param, st));
        });

        plate.addEventListener ('click', (e) =>
        {
            const t = e.target;

            const key = t.closest ('.key[data-param]');
            if (key)
            {
                const id = key.dataset.param;
                if (id === 'output_mode')
                {
                    // Three way: clicking the lit one turns the output stage off entirely.
                    const v = parseInt (key.dataset.value, 10);
                    set (id, P.paramTo0to1 (P.BY_ID[id], index (id) === v ? 0 : v));
                }
                else if (key.dataset.value !== undefined) set (id, P.paramTo0to1 (P.BY_ID[id], parseInt (key.dataset.value, 10)));
                else set (id, st.norm[id] > 0.5 ? 0 : 1);
                return;
            }

            const entry = t.closest ('.plist div');
            if (entry)
            {
                // 8.6: the lit entry again turns Color FX off; any entry while off turns it on as that effect.
                const i = Array.from (entry.parentElement.children).indexOf (entry);
                const wasUni = P.colorIsUnipolar (st), wasFx = P.colorFxIndex (st);
                if (P.colorFxIndex (st) === i) { st.colorOff = true; if (mixer) mixer.setColorFxOff (true); }
                else
                {
                    set ('color_fx_type', P.paramTo0to1 (P.BY_ID.color_fx_type, i));
                    st.colorOff = false; if (mixer) mixer.setColorFxOff (false);
                }
                // Crossing in or out of a riser lands COLOR on that effect's own Off.
                const uni = P.colorIsUnipolar (st);
                if (uni !== wasUni || (uni && P.colorFxIndex (st) !== wasFx))
                    set ('color_amount', P.paramTo0to1 (P.BY_ID.color_amount, uni ? -1 : 0));
                refresh();
                return;
            }

            const arrow = t.closest ('.parw2[data-param]');
            if (arrow)
            {
                const id = arrow.dataset.param, s = P.BY_ID[id], steps = s.end - s.start;
                const next = (index (id) + parseInt (arrow.dataset.step, 10) + steps + 1) % (steps + 1);
                set (id, P.paramTo0to1 (s, next));   // the mixer fetches the reverb bank on the first BOG THROW
                return;
            }

            if (t.closest ('.ico.pw')) { set ('plugin_bypass', on ('plugin_bypass') ? 0 : 1); return; }
            if (t.closest ('.top .glass')) { set ('tempo_source', on ('tempo_source') ? 0 : 1); return; }
            if (t.closest ('.disco')) { setDisco (! disco); return; }
            if (t.closest ('.gear2')) { settings.hidden = ! settings.hidden; e.stopPropagation(); return; }
            if (t.closest ('.plate .logo, .pfoot .wm, .abt.lnk')) window.open ('https://www.colorwithlive.com', '_blank', 'noopener');
        });

        // -- settings: the skin picker ----------------------------------------------------------------

        const settings = $('.setp');
        let skin = 'skin-dark';
        function setSkin (k)
        {
            skin = k;
            SKINS.forEach (([c]) => root.classList.remove (c)); root.classList.remove ('skin-light');
            root.classList.add (k); if (FAMILY[k]) root.classList.add (FAMILY[k]);
            const name = SKINS.find (([c]) => c === k); $('#skinname').textContent = name ? name[1] : k;
            store ('cwl-web-skin', k);
            vignette(); setTimeout (placeScale, 40);
        }
        function vignette ()
        {
            const has = (c) => root.classList.contains (c);
            const book = has ('skin-book'), gold = has ('skin-gold'), light = ['skin-light', 'skin-blue', 'skin-pink'].some (has);
            const on = book || gold || light || ! LIGHT.some (has);
            plate.style.boxShadow = '';
            if (on) { const base = getComputedStyle (plate).boxShadow; plate.style.boxShadow = (base && base !== 'none' ? base + ', ' : '') + (book ? VIG_PAPER : gold ? VIG_GOLD : light ? VIG_LIGHT : VIG); }
        }
        settings.addEventListener ('click', (e) =>
        {
            e.stopPropagation();
            const a = e.target.closest ('[data-sstep]');
            if (a) { const i = Math.max (0, SKINS.findIndex (([c]) => c === skin)); setSkin (SKINS[(i + parseInt (a.dataset.sstep, 10) + SKINS.length) % SKINS.length][0]); }
            if (e.target.closest ('.x')) settings.hidden = true;
        });
        document.addEventListener ('click', (e) => { if (! settings.hidden && ! e.target.closest ('.setp') && ! e.target.closest ('.gear2')) settings.hidden = true; });

        // -- the colour dial and DISCO -----------------------------------------------------------------

        let hue = parseFloat (store ('cwl-web-hue') || '0') || 0;
        function paintHue ()
        {
            const n = STOPS.length - 1, x = clamp01 (hue) * n, i = Math.min (n - 1, Math.floor (x)), t = x - i;
            const c = mixHsl (hex (STOPS[i]), hex (STOPS[i + 1]), t), rgb = (v) => 'rgb(' + v.join (',') + ')', R = root.style;
            R.setProperty ('--scrTx', rgb (lift (c, .45))); R.setProperty ('--scrTx2', rgb (lift (c, .1)));
            R.setProperty ('--scrBg1', rgb (sink (c, .55))); R.setProperty ('--scrBg2', rgb (sink (c, .82)));
            R.setProperty ('--scrGlow', lift (c, .2).join (',')); R.setProperty ('--scrTs', lift (c, .3).join (','));
            R.setProperty ('--scrC', c.join (',')); R.setProperty ('--scrArcA', rgb (lift (c, .28))); R.setProperty ('--scrArcB', rgb (sink (c, .12)));
            R.setProperty ('--scrTint', 'rgba(' + lift (c, .2).join (',') + ',.45)');
            const d = $('.dial'); d.style.background = 'radial-gradient(circle,' + rgb (lift (c, .35)) + ' 0 28%,' + rgb (c) + ' 29% 52%,#111 53%)';
            d.style.setProperty ('--dialang', (-135 + clamp01 (hue) * 270).toFixed (1) + 'deg');
        }

        /*  DISCO: one step round the six colours on every beat of the running strip's position (the
            plugin's DiscoStep), held when nothing is playing. */
        let disco = false, discoStop = -1;
        function setDisco (v) { disco = v; $('.disco').classList.toggle ('on', v); plate.classList.toggle ('disco-on', v); discoStop = -1; }

        // -- the meter, the gain reduction, the scope: the running strip ----------------------------

        const leds = Array.from (bars.children), grSegs = Array.from (grm.children);
        const grValue = $('.gr span');
        let meterDb = -100, holdDb = -100, holdAt = 0, clipAt = -1e9, reductionDb = 0;
        const spectrum = new Float32Array (40), scopeCanvas = $('canvas.scope');
        const FFT_N = 2048, re = new Float64Array (FFT_N), im = new Float64Array (FFT_N);

        function fft (xr, xi)
        {
            const n = xr.length;
            for (let i = 1, j = 0; i < n; i++) { let bit = n >> 1; for (; j & bit; bit >>= 1) j ^= bit; j ^= bit; if (i < j) { [xr[i], xr[j]] = [xr[j], xr[i]]; [xi[i], xi[j]] = [xi[j], xi[i]]; } }
            for (let len = 2; len <= n; len <<= 1)
            {
                const ang = -2 * Math.PI / len, wr = Math.cos (ang), wi = Math.sin (ang);
                for (let i = 0; i < n; i += len)
                {
                    let cr = 1, ci = 0;
                    for (let k = 0; k < len / 2; k++)
                    {
                        const ar = xr[i + k], ai = xi[i + k], br = xr[i + k + len / 2] * cr - xi[i + k + len / 2] * ci, bi = xr[i + k + len / 2] * ci + xi[i + k + len / 2] * cr;
                        xr[i + k] = ar + br; xi[i + k] = ai + bi; xr[i + k + len / 2] = ar - br; xi[i + k + len / 2] = ai - bi;
                        const t = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = t;
                    }
                }
            }
        }

        /*  PlatePanel::updateSpectrum: a Hann window over the newest 2048 samples of the tap, forty
            bands evenly spaced in log frequency from 40 Hz to 16 kHz, a full scale sine at the top,
            rising half the way in a frame and falling an eighth. */
        function updateSpectrum (samples, sr)
        {
            for (let i = 0; i < FFT_N; i++) { re[i] = samples[i] * (0.5 - 0.5 * Math.cos (2 * Math.PI * i / (FFT_N - 1))); im[i] = 0; }
            fft (re, im);
            const lo = 40, hi = Math.min (16000, sr * 0.45);
            for (let band = 0; band < 40; band++)
            {
                const f0 = lo * Math.pow (hi / lo, band / 40), f1 = lo * Math.pow (hi / lo, (band + 1) / 40);
                const b0 = Math.max (1, Math.min (FFT_N / 2 - 1, Math.floor (f0 * FFT_N / sr)));
                const b1 = Math.max (b0 + 1, Math.min (FFT_N / 2, Math.ceil (f1 * FFT_N / sr)));
                let power = 0;
                for (let k = b0; k < b1; k++) power = Math.max (power, Math.hypot (re[k], im[k]));
                const db = 20 * Math.log10 (Math.max (1e-9, power / (FFT_N * 0.25)));
                const target = clamp01 ((db + 62) / 60);
                spectrum[band] += (target - spectrum[band]) * (target > spectrum[band] ? 0.5 : 0.12);
            }
        }

        function drawScope ()
        {
            const cv = scopeCanvas, W = cv.clientWidth, H = cv.clientHeight; if (! W || ! H) return;
            const dpr = window.devicePixelRatio || 1;
            if (cv.width !== Math.round (W * dpr) || cv.height !== Math.round (H * dpr)) { cv.width = Math.round (W * dpr); cv.height = Math.round (H * dpr); }
            const g = cv.getContext ('2d'); g.setTransform (dpr, 0, 0, dpr, 0, 0); g.clearRect (0, 0, W, H);
            const col = (root.style.getPropertyValue ('--scrC') || '63,208,255').trim(), bw = W / 40;
            for (let i = 0; i < 40; i++)
            {
                const h = Math.max (1, spectrum[i] * (H - 2));
                g.fillStyle = 'rgba(' + col + ',.55)'; g.fillRect (i * bw + 0.5, H - h, Math.max (1, bw - 1), h);
                g.fillStyle = 'rgba(' + col + ',.9)'; g.fillRect (i * bw + 0.5, H - h, Math.max (1, bw - 1), 1);
            }
        }

        function readMeters (t, now)
        {
            // PlatePanel::readMeters: +12 on the ladder is full scale, instant up, 24 dB a second down.
            const peak = Math.max (t[0], t[1]);
            const db = peak > 0 ? 20 * Math.log10 (peak) + 12 : -100;
            meterDb = db >= meterDb ? db : Math.max (db, meterDb - 24 / 60);
            if (db >= holdDb) { holdDb = db; holdAt = now; } else if (now - holdAt > 1500) holdDb = meterDb;
            if (t[5] >= 1) clipAt = now;
            const gr = t[4];
            reductionDb = gr >= reductionDb ? gr : Math.max (gr, reductionDb - 20 / 60);
            followingHost = t[11] > 0.5;
            shownBpm = t[6];
        }

        function paintMeters (now)
        {
            const holdIndex = Math.max (0, Math.min (14, Math.floor ((holdDb + 30) / 3)));
            leds.forEach ((s, i) =>
            {
                if (i >= 15) return;
                const lit = meterDb >= -30 + 3 * i, hold = ! lit && holdDb > -99 && holdIndex === i;
                s.className = lit ? 'lit' : hold ? 'hold' : '';
            });
            plate.classList.toggle ('clipped', now - clipAt < 1000);
            const held = Math.abs (reductionDb), lit = Math.max (0, Math.min (12, Math.round (held / 0.5)));
            grSegs.forEach ((s, i) => s.classList.toggle ('lit', i >= 12 - lit));
            setReadout (grValue, P.grText (held));
        }

        let frame = 0;
        function tick (now)
        {
            requestAnimationFrame (tick);
            if (mixer && mixer.ready)
            {
                const withSpectrum = (frame++ & 1) === 0;
                mixer.telemetry (withSpectrum).then ((msg) =>
                {
                    if (! msg || ! msg.ready) return;
                    readMeters (msg.t, performance.now());
                    if (msg.spectrum) updateSpectrum (msg.spectrum, mixer.ctx.sampleRate);
                    if (msg.colorOff !== st.colorOff) { st.colorOff = msg.colorOff; refresh(); }
                    if (disco)
                    {
                        const beat = Math.floor (msg.ppq), stop = mixer.playing ? ((beat % 6) + 6) % 6 : discoStop;
                        if (stop !== discoStop && stop >= 0) { discoStop = stop; hue = stop / 5; paintHue(); }
                    }
                    paintGlass();
                });
            }
            paintMeters (now);
            drawScope();
        }

        // -- open ----------------------------------------------------------------------------------

        /*  The 2.2 mock's fresh open: Premium, Print B, Carbon keys B, Beat FX row F (bft bfe bff),
            ON key A (no onw). Mask 12, Grooves 50 and Glass 50 are the stylesheet's own fallbacks.
            The Beat FX nudges are not, so they are set here at the mock's defaults. Its Nudge list
            and A/B switches are design tools and are not in the demo. */
        root.classList.add ('lit', 'prem', 'ck', 'p5', 'scope', 'bft', 'bfe', 'bff');
        [['--onx', '-16px'], ['--ony', '28px'], ['--dkx', '20px'], ['--bkx', '5px'], ['--bkg', '5px'], ['--bks', '1.050'], ['--spx', '87px']]
            .forEach (([k, v]) => root.style.setProperty (k, v));
        const saved = store ('cwl-web-skin');
        setSkin (SKINS.some (([c]) => c === saved) ? saved : 'skin-dark');
        paintHue();
        refresh();
        placeScale();
        if (document.fonts && document.fonts.ready) document.fonts.ready.then (() => { placeScale(); buildLadder(); });
        window.addEventListener ('resize', placeScale);
        requestAnimationFrame (tick);

        // The engine gets the whole panel the moment it exists (the mixer replays what it was sent).
        if (mixer)
        {
            for (const s of P.SPECS) mixer.setParamNormalised (s.id, st.norm[s.id]);
            mixer.on ('state', () => { if (! mixer.ready) shownBpm = mixer.bpm; paintGlass(); });
            mixer.on ('bank', (b) => { root.classList.toggle ('bank-loading', b === 'loading'); refresh(); });
        }

        return { state: st, set, refresh, setSkin };
    }

    window.CwlPanel = { mount, SKINS };
}) ();
